import { useEffect, useMemo, useState } from "react";
import type { AppState, DailyNote, TaskRecord } from "../types";
import { formatDateLong, formatTime, todayInJapan } from "../lib/date";
import { isScheduledForDate } from "../lib/schedule";
import { CheckIcon } from "./Icons";

interface DateDetailProps {
  date: string;
  state: AppState;
  busyTaskIds: Set<string>;
  noteBusy: boolean;
  onToggle: (taskId: string, completed: boolean) => Promise<void>;
  onSaveNote: (content: string) => Promise<void>;
}

export function DateDetail({
  date,
  state,
  busyTaskIds,
  noteBusy,
  onToggle,
  onSaveNote,
}: DateDetailProps) {
  const records = useMemo(
    () => state.records.filter((record) => record.record_date === date),
    [date, state.records],
  );
  const recordMap = useMemo(() => new Map(records.map((record) => [record.task_id, record])), [records]);
  const tasks = state.tasks.filter((task) => task.is_active || recordMap.has(task.id));
  const note = state.notes.find((item) => item.record_date === date);
  const [noteDraft, setNoteDraft] = useState(note?.content ?? "");
  const isFuture = date > todayInJapan();

  useEffect(() => setNoteDraft(note?.content ?? ""), [date, note?.content]);

  return (
    <section className="date-detail" aria-labelledby="selected-date-heading">
      <div className="date-heading-row">
        <div>
          <p className="eyebrow">CLEANING LOG</p>
          <h2 id="selected-date-heading">{formatDateLong(date)}</h2>
        </div>
        <span className="progress-pill">{records.length}/{tasks.length}</span>
      </div>

      {isFuture && <div className="info-banner">未来の日付は予定のみ確認できます。</div>}

      <div className="task-list">
        {tasks.map((task) => {
          const record = recordMap.get(task.id);
          const member = record ? state.members.find((item) => item.id === record.completed_by) : undefined;
          const scheduled = isScheduledForDate(task, date);
          return (
            <TaskCheckRow
              key={task.id}
              name={task.name}
              record={record}
              memberName={member?.name}
              scheduled={scheduled}
              archived={!task.is_active}
              disabled={isFuture || busyTaskIds.has(task.id) || (!task.is_active && !record)}
              onToggle={() => onToggle(task.id, !record)}
            />
          );
        })}
      </div>

      <div className="note-card">
        <div className="note-title-row">
          <label htmlFor={`note-${date}`}>今日の備考</label>
          <span>{noteDraft.length}/2000</span>
        </div>
        <textarea
          id={`note-${date}`}
          value={noteDraft}
          maxLength={2000}
          rows={4}
          disabled={isFuture}
          placeholder="気づいたことや、次にやることをメモ…"
          onChange={(event) => setNoteDraft(event.target.value)}
        />
        <div className="note-actions">
          {note && (
            <small>
              {state.members.find((item) => item.id === note.updated_by)?.name ?? "利用者"}が更新
            </small>
          )}
          <button
            className="secondary-button"
            disabled={isFuture || noteBusy || noteDraft.trim() === (note?.content ?? "")}
            onClick={() => void onSaveNote(noteDraft)}
          >
            {noteBusy ? "保存中…" : "備考を保存"}
          </button>
        </div>
      </div>
    </section>
  );
}

function TaskCheckRow({
  name,
  record,
  memberName,
  scheduled,
  archived,
  disabled,
  onToggle,
}: {
  name: string;
  record?: TaskRecord;
  memberName?: string;
  scheduled: boolean;
  archived: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button className={`task-check-row ${record ? "is-complete" : ""}`} disabled={disabled} onClick={onToggle}>
      <span className="check-circle">{record && <CheckIcon />}</span>
      <span className="task-check-copy">
        <span className="task-name-line">
          <strong>{name}</strong>
          {scheduled && <span className="due-badge">今日の目安</span>}
          {archived && <span className="archived-badge">終了済み</span>}
        </span>
        <small>{record ? `${memberName ?? "利用者"} ・ ${formatTime(record.completed_at)}` : "まだ記録されていません"}</small>
      </span>
    </button>
  );
}
